import React from 'react';

export default function PropertiesPanel({ selectedComponent, components, updateComponent, onDeleteComponent }) {
  if (!selectedComponent) {
    return (
      <div className="w-64 p-2 border bg-white rounded">
        <h2 className="font-semibold mb-2">Properties</h2>
        <p className="text-gray-500 text-sm">Select a component to edit its properties</p>
      </div>
    );
  }

  const handleChange = (field, value) => {
    console.log(`Updating ${field} to:`, value);
    updateComponent({
      ...selectedComponent,
      properties: {
        ...selectedComponent.properties,
        [field]: value,
      },
    });
  };

  const handleNameChange = (value) => {
    updateComponent({
      ...selectedComponent,
      name: value,
    });
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${selectedComponent.name}"?`)) {
      onDeleteComponent(selectedComponent.id);
    }
  };

  const renderComponentSpecificProperties = () => {
    switch (selectedComponent.type) {
      case 'TextBox':
        return (
          <label className="block">
            <span className="text-sm font-medium">Placeholder:</span>
            <input
              className="w-full border p-1 text-sm"
              value={selectedComponent.properties.placeholder || ''}
              onChange={e => handleChange('placeholder', e.target.value)}
            />
          </label>
        );
      case 'Dropdown':
      case 'ListBox':
        return (
          <label className="block">
            <span className="text-sm font-medium">Options:</span>
            <textarea
              className="w-full border p-1 text-sm"
              rows="3"
              value={selectedComponent.properties.options || ''}
              onChange={e => handleChange('options', e.target.value)}
              placeholder="Option 1&#10;Option 2&#10;Option 3"
            />
          </label>
        );
      case 'ProgressBar':
      case 'Slider':
        return (
          <label className="block">
            <span className="text-sm font-medium">Value:</span>
            <input
              className="w-full border p-1 text-sm"
              type="number"
              min="0"
              max="100"
              value={selectedComponent.properties.value || 50}
              onChange={e => handleChange('value', parseInt(e.target.value) || 0)}
            />
          </label>
        );
      case 'NumericUpDown':
        return (
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium">Value:</span>
              <input
                className="w-full border p-1 text-sm"
                type="number"
                value={selectedComponent.properties.value || 0}
                onChange={e => handleChange('value', parseInt(e.target.value) || 0)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Minimum:</span>
              <input
                className="w-full border p-1 text-sm"
                type="number"
                value={selectedComponent.properties.min || 0}
                onChange={e => handleChange('min', parseInt(e.target.value) || 0)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Maximum:</span>
              <input
                className="w-full border p-1 text-sm"
                type="number"
                value={selectedComponent.properties.max || 100}
                onChange={e => handleChange('max', parseInt(e.target.value) || 100)}
              />
            </label>
          </div>
        );
      case 'RichTextBox':
        return (
          <label className="block">
            <span className="text-sm font-medium">Default Text:</span>
            <textarea
              className="w-full border p-1 text-sm"
              rows="3"
              value={selectedComponent.properties.defaultText || ''}
              onChange={e => handleChange('defaultText', e.target.value)}
              placeholder="Enter default text..."
            />
          </label>
        );
      case 'PictureBox':
        return (
          <label className="block">
            <span className="text-sm font-medium">Image Path:</span>
            <input
              className="w-full border p-1 text-sm"
              value={selectedComponent.properties.imagePath || ''}
              onChange={e => handleChange('imagePath', e.target.value)}
              placeholder="C:\path\to\image.png"
            />
          </label>
        );
      case 'DataGrid':
        return (
          <label className="block">
            <span className="text-sm font-medium">Columns:</span>
            <textarea
              className="w-full border p-1 text-sm"
              rows="3"
              value={selectedComponent.properties.columns || ''}
              onChange={e => handleChange('columns', e.target.value)}
              placeholder="Column 1&#10;Column 2&#10;Column 3"
            />
          </label>
        );
      case 'TreeView':
        return (
          <label className="block">
            <span className="text-sm font-medium">Tree Items:</span>
            <textarea
              className="w-full border p-1 text-sm"
              rows="3"
              value={selectedComponent.properties.treeItems || ''}
              onChange={e => handleChange('treeItems', e.target.value)}
              placeholder="Root&#10;  Child 1&#10;  Child 2"
            />
          </label>
        );
      case 'ListView':
        return (
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium">Columns:</span>
              <textarea
                className="w-full border p-1 text-sm"
                rows="3"
                value={selectedComponent.properties.columns || ''}
                onChange={e => handleChange('columns', e.target.value)}
                placeholder="Name&#10;Type&#10;Size&#10;Status"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">View Mode:</span>
              <select
                className="w-full border p-1 text-sm"
                value={selectedComponent.properties.viewMode || 'Details'}
                onChange={e => handleChange('viewMode', e.target.value)}
              >
                <option value="Details">Details</option>
                <option value="List">List</option>
                <option value="LargeIcon">Large Icons</option>
                <option value="SmallIcon">Small Icons</option>
                <option value="Tile">Tile</option>
              </select>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="multiSelect"
                checked={selectedComponent.properties.multiSelect !== false}
                onChange={e => handleChange('multiSelect', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="multiSelect" className="text-sm font-medium">
                Multi-Select
              </label>
            </div>
          </div>
        );
      case 'ComboBox':
        return (
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium">Options:</span>
              <textarea
                className="w-full border p-1 text-sm"
                rows="3"
                value={selectedComponent.properties.options || ''}
                onChange={e => handleChange('options', e.target.value)}
                placeholder="Option 1&#10;Option 2&#10;Option 3"
              />
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="editable"
                checked={selectedComponent.properties.editable !== false}
                onChange={e => handleChange('editable', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="editable" className="text-sm font-medium">
                Editable
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="autoComplete"
                checked={selectedComponent.properties.autoComplete !== false}
                onChange={e => handleChange('autoComplete', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="autoComplete" className="text-sm font-medium">
                Auto-Complete
              </label>
            </div>
          </div>
        );
      case 'CheckedListBox':
        return (
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium">Items:</span>
              <textarea
                className="w-full border p-1 text-sm"
                rows="3"
                value={selectedComponent.properties.items || ''}
                onChange={e => handleChange('items', e.target.value)}
                placeholder="Item 1&#10;Item 2&#10;Item 3"
              />
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="threeState"
                checked={selectedComponent.properties.threeState !== false}
                onChange={e => handleChange('threeState', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="threeState" className="text-sm font-medium">
                Three-State Checkboxes
              </label>
            </div>
          </div>
        );
      case 'SplitContainer':
        return (
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium">Orientation:</span>
              <select
                className="w-full border p-1 text-sm"
                value={selectedComponent.properties.orientation || 'Horizontal'}
                onChange={e => handleChange('orientation', e.target.value)}
              >
                <option value="Horizontal">Horizontal</option>
                <option value="Vertical">Vertical</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium">Splitter Distance:</span>
              <input
                className="w-full border p-1 text-sm"
                type="number"
                min="0"
                max="100"
                value={selectedComponent.properties.splitterDistance || 50}
                onChange={e => handleChange('splitterDistance', parseInt(e.target.value) || 50)}
              />
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="fixedPanel"
                checked={selectedComponent.properties.fixedPanel !== false}
                onChange={e => handleChange('fixedPanel', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="fixedPanel" className="text-sm font-medium">
                Fixed Panel
              </label>
            </div>
          </div>
        );
      case 'CommandOutput':
        return (
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium">Default Command:</span>
              <input
                className="w-full border p-1 text-sm"
                value={selectedComponent.properties.defaultCommand || ''}
                onChange={e => handleChange('defaultCommand', e.target.value)}
                placeholder="Get-Process"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Output Format:</span>
              <select
                className="w-full border p-1 text-sm"
                value={selectedComponent.properties.outputFormat || 'Table'}
                onChange={e => handleChange('outputFormat', e.target.value)}
              >
                <option value="Table">Table</option>
                <option value="List">List</option>
                <option value="Wide">Wide</option>
                <option value="Custom">Custom</option>
              </select>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="autoExecute"
                checked={selectedComponent.properties.autoExecute !== false}
                onChange={e => handleChange('autoExecute', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="autoExecute" className="text-sm font-medium">
                Auto-Execute on Load
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="showErrors"
                checked={selectedComponent.properties.showErrors !== false}
                onChange={e => handleChange('showErrors', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="showErrors" className="text-sm font-medium">
                Show Error Output
              </label>
            </div>
          </div>
        );
      case 'ParameterPanel':
        return (
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium">Command Name:</span>
              <input
                className="w-full border p-1 text-sm"
                value={selectedComponent.properties.commandName || ''}
                onChange={e => handleChange('commandName', e.target.value)}
                placeholder="Get-Process"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Parameters:</span>
              <textarea
                className="w-full border p-1 text-sm"
                rows="3"
                value={selectedComponent.properties.parameters || ''}
                onChange={e => handleChange('parameters', e.target.value)}
                placeholder="Name,string,Process name&#10;Id,int,Process ID&#10;IncludeUsername,bool,Include username"
              />
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="showHelp"
                checked={selectedComponent.properties.showHelp !== false}
                onChange={e => handleChange('showHelp', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="showHelp" className="text-sm font-medium">
                Show Help Text
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="validateInput"
                checked={selectedComponent.properties.validateInput !== false}
                onChange={e => handleChange('validateInput', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="validateInput" className="text-sm font-medium">
                Validate Input
              </label>
            </div>
          </div>
        );
      case 'TabControl':
        return (
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium">Tab Names:</span>
              <textarea
                className="w-full border p-1 text-sm"
                rows="3"
                value={selectedComponent.properties.tabNames || ''}
                onChange={e => handleChange('tabNames', e.target.value)}
                placeholder="Tab 1&#10;Tab 2&#10;Tab 3"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Active Tab:</span>
              <select
                className="w-full border p-1 text-sm"
                value={selectedComponent.properties.activeTab || 0}
                onChange={e => handleChange('activeTab', parseInt(e.target.value) || 0)}
              >
                {selectedComponent.properties.tabNames ? 
                  selectedComponent.properties.tabNames.split('\n').filter(name => name.trim()).map((tabName, index) => (
                    <option key={index} value={index}>{tabName.trim()}</option>
                  ))
                  : (
                    <>
                      <option value={0}>Tab 1</option>
                      <option value={1}>Tab 2</option>
                    </>
                  )
                }
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium">Tab Content:</span>
              <textarea
                className="w-full border p-1 text-sm"
                rows="3"
                value={selectedComponent.properties.tabContent || ''}
                onChange={e => handleChange('tabContent', e.target.value)}
                placeholder="Content for each tab (separate with |)"
              />
            </label>
          </div>
        );
      case 'DateTimePicker':
        return (
          <label className="block">
            <span className="text-sm font-medium">Date Value:</span>
            <input
              className="w-full border p-1 text-sm"
              type="date"
              value={selectedComponent.properties.value || new Date().toISOString().split('T')[0]}
              onChange={e => handleChange('value', e.target.value)}
            />
          </label>
        );
      case 'Form':
        return (
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium">Form Title:</span>
              <input
                className="w-full border p-1 text-sm"
                value={selectedComponent.properties.formTitle || ''}
                onChange={e => handleChange('formTitle', e.target.value)}
                placeholder="Enter form title..."
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Window Location:</span>
              <select
                className="w-full border p-1 text-sm"
                value={selectedComponent.properties.windowStartupLocation || 'CenterScreen'}
                onChange={e => handleChange('windowStartupLocation', e.target.value)}
              >
                <option value="CenterScreen">Center Screen</option>
                <option value="CenterOwner">Center Owner</option>
                <option value="Manual">Manual</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium">Background Color:</span>
              <select
                className="w-full border p-1 text-sm"
                value={selectedComponent.properties.backgroundColor || 'White'}
                onChange={e => handleChange('backgroundColor', e.target.value)}
              >
                <option value="White">White</option>
                <option value="LightGray">Light Gray</option>
                <option value="LightBlue">Light Blue</option>
                <option value="LightGreen">Light Green</option>
                <option value="Transparent">Transparent</option>
              </select>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="resizable"
                checked={selectedComponent.properties.resizable !== false}
                onChange={e => handleChange('resizable', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="resizable" className="text-sm font-medium">
                Resizable Window
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="maximizable"
                checked={selectedComponent.properties.maximizable !== false}
                onChange={e => handleChange('maximizable', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="maximizable" className="text-sm font-medium">
                Maximizable Window
              </label>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-64 p-2 border bg-white rounded">
      <div className="flex justify-between items-center mb-2">
        <h2 className="font-semibold">Properties</h2>
        <button
          onClick={handleDelete}
          className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
          title="Delete component"
        >
          Delete
        </button>
      </div>
      <div className="space-y-2">
        <label className="block">
          <span className="text-sm font-medium">Name:</span>
          <input
            className="w-full border p-1 text-sm"
            value={selectedComponent.name}
            onChange={e => handleNameChange(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Text:</span>
          <input
            className="w-full border p-1 text-sm"
            value={selectedComponent.properties.text}
            onChange={e => handleChange('text', e.target.value)}
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-sm font-medium">X:</span>
            <input
              className="w-full border p-1 text-sm"
              type="number"
              value={selectedComponent.properties.x}
              onChange={e => handleChange('x', parseInt(e.target.value) || 0)}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Y:</span>
            <input
              className="w-full border p-1 text-sm"
              type="number"
              value={selectedComponent.properties.y}
              onChange={e => handleChange('y', parseInt(e.target.value) || 0)}
            />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-sm font-medium">Width:</span>
            <input
              className="w-full border p-1 text-sm"
              type="number"
              value={selectedComponent.properties.width}
              onChange={e => handleChange('width', parseInt(e.target.value) || 0)}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Height:</span>
            <input
              className="w-full border p-1 text-sm"
              type="number"
              value={selectedComponent.properties.height}
              onChange={e => handleChange('height', parseInt(e.target.value) || 0)}
            />
          </label>
        </div>
        {/* Visual Properties */}
        <div className="border-t pt-2 mt-2">
          <h3 className="text-sm font-medium mb-2">Visual Properties</h3>
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium">Background Color:</span>
              <select
                className="w-full border p-1 text-sm"
                value={selectedComponent.properties.backgroundColor || 'default'}
                onChange={e => handleChange('backgroundColor', e.target.value)}
              >
                <option value="default">Default</option>
                <option value="White">White</option>
                <option value="LightGray">Light Gray</option>
                <option value="LightBlue">Light Blue</option>
                <option value="LightGreen">Light Green</option>
                <option value="LightYellow">Light Yellow</option>
                <option value="LightPink">Light Pink</option>
                <option value="Transparent">Transparent</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium">Text Color:</span>
              <select
                className="w-full border p-1 text-sm"
                value={selectedComponent.properties.textColor || 'default'}
                onChange={e => handleChange('textColor', e.target.value)}
              >
                <option value="default">Default</option>
                <option value="Black">Black</option>
                <option value="White">White</option>
                <option value="Red">Red</option>
                <option value="Blue">Blue</option>
                <option value="Green">Green</option>
                <option value="Gray">Gray</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium">Border Style:</span>
              <select
                className="w-full border p-1 text-sm"
                value={selectedComponent.properties.borderStyle || 'default'}
                onChange={e => handleChange('borderStyle', e.target.value)}
              >
                <option value="default">Default</option>
                <option value="None">None</option>
                <option value="Solid">Solid</option>
                <option value="Dashed">Dashed</option>
                <option value="Dotted">Dotted</option>
              </select>
            </label>
          </div>
        </div>
        
        {renderComponentSpecificProperties()}
        
        {/* Tab Association (only show if there are TabControls) */}
        {(() => {
          const tabControls = components.filter(c => c.type === 'TabControl');
          if (tabControls.length > 0) {
            return (
              <label className="block">
                <span className="text-sm font-medium">Associated Tab:</span>
                <select
                  className="w-full border p-1 text-sm"
                  value={selectedComponent.parentTab || 'none'}
                  onChange={e => {
                    const newParentTab = e.target.value === 'none' ? null : e.target.value;
                    const selectedTabControl = tabControls.find(tc => tc.id === newParentTab);
                    const tabIndex = selectedTabControl ? (selectedTabControl.properties.activeTab || 0) : null;
                    
                    updateComponent({
                      ...selectedComponent,
                      parentTab: newParentTab,
                      tabIndex: tabIndex
                    });
                  }}
                >
                  <option value="none">No Tab (Always Visible)</option>
                  {tabControls.map(tabControl => {
                    const tabNames = tabControl.properties.tabNames ? 
                      tabControl.properties.tabNames.split('\n').filter(name => name.trim()) : 
                      ['Tab 1', 'Tab 2'];
                    
                    return tabNames.map((tabName, index) => (
                      <option key={`${tabControl.id}-${index}`} value={tabControl.id}>
                        {tabControl.name} - {tabName.trim()}
                      </option>
                    ));
                  }).flat()}
                </select>
              </label>
            );
          }
          return null;
        })()}
        
        {/* Form Association (only show if there are Forms) */}
        {(() => {
          const forms = components.filter(c => c.type === 'Form');
          if (forms.length > 0) {
            return (
              <label className="block">
                <span className="text-sm font-medium">Associated Form:</span>
                <select
                  className="w-full border p-1 text-sm"
                  value={selectedComponent.parentForm || 'none'}
                  onChange={e => {
                    const newParentForm = e.target.value === 'none' ? null : e.target.value;
                    updateComponent({
                      ...selectedComponent,
                      parentForm: newParentForm
                    });
                  }}
                >
                  <option value="none">No Form (Canvas Level)</option>
                  {forms.map(form => (
                    <option key={form.id} value={form.id}>
                      {form.name}
                    </option>
                  ))}
                </select>
              </label>
            );
          }
          return null;
        })()}
      </div>
    </div>
  );
}
